import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Job } from '@volt/shared';
import { useSession } from '../state/session.js';
import { buildLibraryItems, type LibraryItem, type LibraryKind } from '../lib/generatedLibrary.js';

const PAGE_SIZE = 24;

export interface GeneratedLibrary {
  items: LibraryItem[];
  /** True once at least one reusable result exists — drives whether "From library" is offered at all. */
  hasItems: boolean;
  loading: boolean;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refresh: () => Promise<void>;
}

/**
 * The user's finished generations (images, video, audio) from every section,
 * offered as reusable references. Nothing is requested until `enabled`, so a
 * screen that only needs the library after a tap does not pay for it up front.
 */
export function useGeneratedLibrary(
  kinds: readonly LibraryKind[],
  options: { enabled?: boolean } = {},
): GeneratedLibrary {
  const { enabled = true } = options;
  const { api, data } = useSession();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const cursorRef = useRef<string | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.listJobs({ limit: PAGE_SIZE });
      if (!mounted.current) return;
      cursorRef.current = response.nextCursor;
      setHasMore(response.hasMore);
      // Keep older pages that were already scrolled into view.
      setJobs((prev) => {
        const head = new Set(response.jobs.map((job) => job.id));
        return [...response.jobs, ...prev.filter((job) => !head.has(job.id))];
      });
    } catch {
      /* the library is a convenience: keep whatever was loaded */
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [api]);

  const loadMore = useCallback(async () => {
    const cursor = cursorRef.current;
    if (!cursor) return;
    setLoading(true);
    try {
      const response = await api.listJobs({ limit: PAGE_SIZE, cursor });
      if (!mounted.current) return;
      cursorRef.current = response.nextCursor;
      setHasMore(response.hasMore);
      setJobs((prev) => {
        const seen = new Set(prev.map((job) => job.id));
        return [...prev, ...response.jobs.filter((job) => !seen.has(job.id))];
      });
    } catch {
      /* ignore */
    } finally {
      if (mounted.current) setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (enabled) void refresh();
  }, [enabled, refresh]);

  const kindsKey = kinds.join(',');
  const models = data?.models;
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `kindsKey` stands in for the array identity
  const items = useMemo(() => buildLibraryItems(jobs, models ?? [], kinds), [jobs, models, kindsKey]);

  return { items, hasItems: items.length > 0, loading, hasMore, loadMore, refresh };
}
